import { z } from 'zod'
import { parseDataOfType } from '@/common/parseDataType'
import { apiErrorHandler } from '../errorHandler'

export const DocumentAgentType = {
  Proposal: 'proposal',
  Presentation: 'presentation'
} as const

export const DocumentAgentTypeSchema = z.enum(DocumentAgentType)
export type DocumentAgentType = z.infer<typeof DocumentAgentTypeSchema>

export const DocumentAgentTypeDefinitionSchema = z.object({
  type: DocumentAgentTypeSchema,
  label: z.string(),
  description: z.string(),
  outputFormat: z.string()
})
export type DocumentAgentTypeDefinition = z.infer<typeof DocumentAgentTypeDefinitionSchema>

export const ListDocumentAgentTypesResponseSchema = z.object({
  types: z.array(DocumentAgentTypeDefinitionSchema)
})
export type ListDocumentAgentTypesResponse = z.infer<typeof ListDocumentAgentTypesResponseSchema>

export const DocumentAgentArtifactSchema = z.object({
  artifactType: DocumentAgentTypeSchema,
  artifactId: z.number(),
  title: z.string(),
  status: z.string(),
  updatedAtTsMs: z.number()
})
export type DocumentAgentArtifact = z.infer<typeof DocumentAgentArtifactSchema>

export const ListDocumentAgentArtifactsResponseSchema = z.object({
  artifacts: z.array(DocumentAgentArtifactSchema)
})
export type ListDocumentAgentArtifactsResponse = z.infer<typeof ListDocumentAgentArtifactsResponseSchema>

export const ProposalDefaultTemplateSchema = z.object({
  docId: z.number(),
  fileName: z.string()
})
export type ProposalDefaultTemplate = z.infer<typeof ProposalDefaultTemplateSchema>

export const ProposalDefaultTemplateResponseSchema = z.object({
  template: ProposalDefaultTemplateSchema.nullable()
})
export type ProposalDefaultTemplateResponse = z.infer<typeof ProposalDefaultTemplateResponseSchema>

export const PresentationDefaultTemplateSchema = z.object({
  docId: z.number(),
  fileName: z.string()
})
export type PresentationDefaultTemplate = z.infer<typeof PresentationDefaultTemplateSchema>

export const PresentationDefaultTemplateResponseSchema = z.object({
  template: PresentationDefaultTemplateSchema.nullable()
})
export type PresentationDefaultTemplateResponse = z.infer<typeof PresentationDefaultTemplateResponseSchema>

export async function listDocumentAgentTypes(projectId: number): Promise<ListDocumentAgentTypesResponse> {
  const response = await fetch(`/api/projects/${projectId}/document-agent/types`, {
    method: 'GET',
    credentials: 'same-origin'
  })
  await apiErrorHandler(response)
  const data: unknown = await response.json()
  return parseDataOfType(data, ListDocumentAgentTypesResponseSchema)
}

export async function listDocumentAgentArtifacts(projectId: number): Promise<ListDocumentAgentArtifactsResponse> {
  const response = await fetch(`/api/projects/${projectId}/document-agent/artifacts`, {
    method: 'GET',
    credentials: 'same-origin'
  })
  await apiErrorHandler(response)
  const data: unknown = await response.json()
  return parseDataOfType(data, ListDocumentAgentArtifactsResponseSchema)
}

export async function getProposalDefaultTemplate(): Promise<ProposalDefaultTemplateResponse> {
  const response = await fetch('/api/org/document-agent/defaults/proposal-template', {
    method: 'GET',
    credentials: 'same-origin'
  })
  await apiErrorHandler(response)
  const data: unknown = await response.json()
  return parseDataOfType(data, ProposalDefaultTemplateResponseSchema)
}

export async function setProposalDefaultTemplate(docId: number): Promise<ProposalDefaultTemplateResponse> {
  const response = await fetch('/api/org/document-agent/defaults/proposal-template', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ docId })
  })
  await apiErrorHandler(response)
  const data: unknown = await response.json()
  return parseDataOfType(data, ProposalDefaultTemplateResponseSchema)
}

export async function clearProposalDefaultTemplate(): Promise<void> {
  const response = await fetch('/api/org/document-agent/defaults/proposal-template', {
    method: 'DELETE',
    credentials: 'same-origin'
  })
  await apiErrorHandler(response)
}

export async function getPresentationDefaultTemplate(): Promise<PresentationDefaultTemplateResponse> {
  const response = await fetch('/api/org/document-agent/defaults/presentation-template', {
    method: 'GET',
    credentials: 'same-origin'
  })
  await apiErrorHandler(response)
  const data: unknown = await response.json()
  return parseDataOfType(data, PresentationDefaultTemplateResponseSchema)
}

export async function setPresentationDefaultTemplate(docId: number): Promise<PresentationDefaultTemplateResponse> {
  const response = await fetch('/api/org/document-agent/defaults/presentation-template', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ docId })
  })
  await apiErrorHandler(response)
  const data: unknown = await response.json()
  return parseDataOfType(data, PresentationDefaultTemplateResponseSchema)
}

export async function clearPresentationDefaultTemplate(): Promise<void> {
  const response = await fetch('/api/org/document-agent/defaults/presentation-template', {
    method: 'DELETE',
    credentials: 'same-origin'
  })
  await apiErrorHandler(response)
}
